import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Users, Award, BookOpen, HelpCircle, TrendingUp } from "lucide-react";

const Community = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  const communityFeatures = [
    {
      id: "forum",
      title: "Discussion Forum",
      description: "Connect with peers, share ideas, and participate in meaningful discussions",
      icon: MessageSquare,
      color: "from-blue-500 to-blue-600",
      stats: "1.2k+ discussions",
      route: "/community/forum"
    },
    {
      id: "mentoring",
      title: "Peer Mentoring",
      description: "Get guidance from experienced mentors or become a mentor yourself",
      icon: Users,
      color: "from-purple-500 to-purple-600",
      stats: "500+ mentors",
      route: "/community/mentoring"
    },
    {
      id: "success",
      title: "Success Stories",
      description: "Read inspiring success stories and share your own achievements",
      icon: Award,
      color: "from-yellow-500 to-yellow-600",
      stats: "300+ stories",
      route: "/community/success-stories"
    },
    {
      id: "groups",
      title: "Study Groups",
      description: "Join or create study groups to learn together",
      icon: BookOpen,
      color: "from-green-500 to-green-600",
      stats: "150+ groups",
      route: "/community/study-groups"
    },
    {
      id: "qa",
      title: "Q&A Section",
      description: "Ask questions, share knowledge, and help others learn",
      icon: HelpCircle,
      color: "from-red-500 to-red-600",
      stats: "2k+ questions",
      route: "/community/qa"
    },
    {
      id: "leaderboard",
      title: "Community Leaderboard",
      description: "See top contributors and earn recognition for your participation",
      icon: TrendingUp,
      color: "from-indigo-500 to-indigo-600",
      stats: "Top 100 listed",
      route: "/leaderboard"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-primary to-primary/80 mb-6 shadow-lg">
            <Users className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Community Hub
          </h1>
          <p className="text-gray-600 text-lg md:text-xl max-w-2xl mx-auto">
            Connect, learn, and grow together with thousands of students and mentors
          </p>
        </div>

        {/* Community Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {communityFeatures.map((feature) => {
            const Icon = feature.icon;
            return (
              <Card
                key={feature.id}
                className="group border-none shadow-lg hover:shadow-2xl cursor-pointer transition-all duration-300 overflow-hidden"
                onClick={() => navigate(feature.route)}
              >
                <div className={`h-2 bg-gradient-to-r ${feature.color}`}></div>
                <CardHeader className="pb-4">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4 shadow-md`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-xl mb-2 group-hover:text-primary transition-colors">
                    {feature.title}
                  </CardTitle>
                  <CardDescription className="text-sm leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs">
                      {feature.stats}
                    </Badge>
                    <Button variant="ghost" size="sm" className="group-hover:bg-primary group-hover:text-white transition-colors">
                      Explore →
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Community Guidelines */}
        <Card className="mt-12 border-none shadow-md bg-primary/5">
          <CardHeader>
            <CardTitle>Community Guidelines</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span>Be respectful and considerate to all community members</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span>Share knowledge freely and help others learn</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span>No spam, harassment, or inappropriate content</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span>Give credit where credit is due</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold">•</span>
                <span>Report any violations to moderators</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default Community;
